import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, Check, AlertTriangle, ChevronLeft, Plus, Wifi, WifiOff, QrCode, X, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
}

const OFFLINE_QUEUE_KEY = "crm_scanner_offline_queue";

function loadOfflineQueue() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY)) || []; } catch { return []; }
}
function saveOfflineQueue(queue) { localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue)); }

// Top-level stable Field component (must be OUTSIDE parent to prevent focus loss on each keystroke)
function Field({ label, name, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-600">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        className="h-12 text-base"
        data-testid={`scanner-${name}`}
      />
    </div>
  );
}

export default function ScannerPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle, processing, review, saving, saved
  const [scanData, setScanData] = useState(null);
  const [form, setForm] = useState({
    firma: "", vorname: "", nachname: "", position: "",
    telefon: "", mobil: "", email: "", webseite: "",
    strasse: "", plz: "", ort: "", card_image_path: null,
  });
  const [duplicates, setDuplicates] = useState([]);
  const [offlineQueue, setOfflineQueue] = useState(loadOfflineQueue);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showQR, setShowQR] = useState(false);
  const [scanCount, setScanCount] = useState(0);

  // Online/offline listener
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Get current user name for verantwortlicher
  const [currentUser, setCurrentUser] = useState("");
  useEffect(() => {
    authFetch(`${API}/auth/me`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setCurrentUser(d.name || "");
    }).catch(() => {});
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setPhase("processing");

    if (!isOnline) {
      // Offline: store image as base64 and go to review with empty data
      const reader = new FileReader();
      reader.onload = () => {
        setScanData({ offlineImage: reader.result, fileName: file.name });
        setForm({ firma: "", vorname: "", nachname: "", position: "", telefon: "", mobil: "", email: "", webseite: "", strasse: "", plz: "", ort: "", card_image_path: null });
        setPhase("review");
        toast.info("Offline-Modus: Bitte Daten manuell eingeben");
      };
      reader.readAsDataURL(file);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/crm/scan-business-card`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setScanData(data);
        setForm({
          firma: data.firma || "",
          vorname: data.vorname || "",
          nachname: data.nachname || "",
          position: data.position || "",
          telefon: data.telefon || "",
          mobil: data.mobil || "",
          email: data.email || "",
          webseite: data.webseite || "",
          strasse: data.adresse || data.strasse || "",
          plz: "", ort: "",
          card_image_path: data.card_image_path || null,
        });
        toast.success("Visitenkarte erkannt!");
        // Check for duplicates
        checkDuplicates(data.firma, data.telefon);
        setPhase("review");
      } else {
        toast.error("Scan fehlgeschlagen");
        setPhase("idle");
      }
    } catch {
      toast.error("Fehler beim Scannen");
      setPhase("idle");
    }
  };

  const checkDuplicates = async (firma, telefon) => {
    try {
      const res = await authFetch(`${API}/crm/contacts/check-duplicate`, {
        method: "POST",
        body: JSON.stringify({ firma: firma || "", telefon: telefon || "" }),
      });
      if (res.ok) {
        const data = await res.json();
        setDuplicates(data.duplicates || []);
      }
    } catch {}
  };

  const saveContact = async (quick = false) => {
    if (!form.firma && !form.vorname && !form.nachname) {
      toast.error("Mindestens Firma oder Name erforderlich");
      return;
    }

    // Offline save
    if (!isOnline) {
      const entry = { ...form, verantwortlicher: currentUser, savedAt: new Date().toISOString() };
      const queue = [...offlineQueue, entry];
      setOfflineQueue(queue);
      saveOfflineQueue(queue);
      toast.success("Offline gespeichert! Wird synchronisiert wenn online.");
      setScanCount(c => c + 1);
      resetForNext();
      return;
    }

    setPhase("saving");
    try {
      const payload = {
        ...form,
        kontaktart: "Interessent",
        verantwortlicher: currentUser,
      };
      const res = await authFetch(`${API}/crm/contacts`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Kontakt gespeichert!");
        setScanCount(c => c + 1);
        setPhase("saved");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Speichern");
        setPhase("review");
      }
    } catch {
      toast.error("Fehler beim Speichern");
      setPhase("review");
    }
  };

  const resetForNext = () => {
    setPhase("idle");
    setScanData(null);
    setDuplicates([]);
    setForm({ firma: "", vorname: "", nachname: "", position: "", telefon: "", mobil: "", email: "", webseite: "", strasse: "", plz: "", ort: "", card_image_path: null });
    if (fileRef.current) fileRef.current.value = "";
  };

  const syncOfflineQueue = useCallback(async () => {
    if (!isOnline || offlineQueue.length === 0) return;
    let synced = 0;
    const remaining = [];
    for (const entry of offlineQueue) {
      try {
        const res = await authFetch(`${API}/crm/contacts`, {
          method: "POST",
          body: JSON.stringify({ ...entry, kontaktart: "Interessent" }),
        });
        if (res.ok) synced++;
        else remaining.push(entry);
      } catch {
        remaining.push(entry);
      }
    }
    setOfflineQueue(remaining);
    saveOfflineQueue(remaining);
    if (synced > 0) toast.success(`${synced} Kontakt(e) synchronisiert!`);
    if (remaining.length > 0) toast.warning(`${remaining.length} Kontakt(e) konnten nicht synchronisiert werden`);
  }, [isOnline, offlineQueue]);

  const scannerUrl = `${window.location.origin}/app/scanner`;

  // IMPORTANT: defined inline, so we use a stable function reference via useCallback
  // to prevent input remount and focus loss on every keystroke
  const updateForm = useCallback((name, val) => setForm(p => ({ ...p, [name]: val })), []);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="scanner-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0 safe-area-top">
        <button onClick={() => navigate("/app/crm")} className="flex items-center gap-1 text-gray-600" data-testid="scanner-back-btn">
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">CRM</span>
        </button>
        <h1 className="text-base font-semibold text-gray-900">Messe Scanner</h1>
        <div className="flex items-center gap-2">
          {offlineQueue.length > 0 && (
            <button onClick={syncOfflineQueue} className="relative" data-testid="sync-btn">
              {isOnline ? <Wifi className="w-5 h-5 text-green-500" /> : <WifiOff className="w-5 h-5 text-red-500" />}
              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{offlineQueue.length}</span>
            </button>
          )}
          <button onClick={() => setShowQR(!showQR)} className="text-gray-500" data-testid="qr-toggle-btn">
            <QrCode className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* QR Code Overlay */}
      {showQR && (
        <div className="absolute inset-0 z-50 bg-white flex flex-col items-center justify-center p-8" data-testid="qr-overlay">
          <button onClick={() => setShowQR(false)} className="absolute top-4 right-4 text-gray-400">
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scanner-Link teilen</h2>
          <div className="p-4 bg-white border-2 border-gray-200 rounded-2xl">
            <QRCodeSVG value={scannerUrl} size={200} level="M" />
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center max-w-xs">QR-Code scannen oder Link teilen:</p>
          <div className="mt-2 flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 w-full max-w-xs">
            <span className="text-xs text-gray-600 truncate flex-1">{scannerUrl}</span>
            <button onClick={() => { navigator.clipboard.writeText(scannerUrl); toast.success("Link kopiert!"); }} className="text-blue-600 text-xs font-medium flex-shrink-0" data-testid="copy-link-btn">
              Kopieren
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* IDLE: Camera Prompt */}
        {phase === "idle" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="scanner-file-input"
            />
            <div className="w-24 h-24 rounded-full bg-blue-50 flex items-center justify-center mb-6">
              <Camera className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Visitenkarte scannen</h2>
            <p className="text-sm text-gray-500 text-center mb-8 max-w-xs">
              Fotografieren Sie eine Visitenkarte. Die Kontaktdaten werden automatisch erkannt.
            </p>
            <Button
              onClick={() => fileRef.current?.click()}
              className="w-full max-w-xs h-14 text-lg rounded-xl"
              data-testid="scanner-capture-btn"
            >
              <Camera className="w-6 h-6 mr-3" /> Foto aufnehmen
            </Button>
            {scanCount > 0 && (
              <p className="mt-4 text-sm text-gray-400">{scanCount} Kontakt(e) erfasst</p>
            )}
            {!isOnline && (
              <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm">
                <WifiOff className="w-4 h-4" />
                <span>Offline-Modus aktiv</span>
              </div>
            )}
          </div>
        )}

        {/* PROCESSING: Spinner */}
        {phase === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
            <p className="text-lg font-medium text-gray-700">Wird analysiert...</p>
            <p className="text-sm text-gray-400 mt-1">Kontaktdaten werden erkannt</p>
          </div>
        )}

        {/* REVIEW: Editable Form */}
        {phase === "review" && (
          <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
            {/* Duplicate Warning */}
            {duplicates.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="duplicate-warning">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Mögliche Duplikate gefunden:</p>
                  {duplicates.map((d) => (
                    <p key={d.id} className="text-xs text-amber-700 mt-0.5">
                      {d.firma} {d.telefon && `(${d.telefon})`} {d.kundennummer && `- ${d.kundennummer}`}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <Field label="Firma" name="firma" value={form.firma} onChange={updateForm} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vorname" name="vorname" value={form.vorname} onChange={updateForm} />
              <Field label="Nachname" name="nachname" value={form.nachname} onChange={updateForm} />
            </div>
            <Field label="Position" name="position" value={form.position} onChange={updateForm} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Telefon" name="telefon" type="tel" value={form.telefon} onChange={updateForm} />
              <Field label="Mobil" name="mobil" type="tel" value={form.mobil} onChange={updateForm} />
            </div>
            <Field label="E-Mail" name="email" type="email" value={form.email} onChange={updateForm} />
            <Field label="Website" name="webseite" type="url" value={form.webseite} onChange={updateForm} />
            <Field label="Straße" name="strasse" value={form.strasse} onChange={updateForm} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="PLZ" name="plz" value={form.plz} onChange={updateForm} />
              <div className="col-span-2">
                <Field label="Ort" name="ort" value={form.ort} onChange={updateForm} />
              </div>
            </div>
          </div>
        )}

        {/* SAVING: Spinner */}
        {phase === "saving" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
            <p className="text-lg font-medium text-gray-700">Wird gespeichert...</p>
          </div>
        )}

        {/* SAVED: Success */}
        {phase === "saved" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Kontakt gespeichert!</h2>
            <p className="text-sm text-gray-500 mb-6">{form.firma || [form.vorname, form.nachname].filter(Boolean).join(" ")}</p>
            <Button
              onClick={resetForNext}
              className="w-full max-w-xs h-14 text-lg rounded-xl"
              data-testid="scanner-next-btn"
            >
              <Camera className="w-6 h-6 mr-3" /> Nächste scannen
            </Button>
            <p className="mt-3 text-sm text-gray-400">{scanCount} Kontakt(e) erfasst</p>
          </div>
        )}
      </div>

      {/* Bottom Action Bar (Review phase) */}
      {phase === "review" && (
        <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0 safe-area-bottom" data-testid="scanner-actions">
          <div className="flex gap-3 max-w-lg mx-auto">
            <Button
              variant="outline"
              onClick={resetForNext}
              className="flex-1 h-12 text-base rounded-xl"
              data-testid="scanner-discard-btn"
            >
              Verwerfen
            </Button>
            <Button
              onClick={() => saveContact(true)}
              className="flex-1 h-12 text-base rounded-xl bg-green-600 hover:bg-green-700"
              data-testid="scanner-quick-save-btn"
            >
              <Zap className="w-5 h-5 mr-2" /> Schnell speichern
            </Button>
          </div>
          <Button
            variant="ghost"
            onClick={() => saveContact(false)}
            className="w-full max-w-lg mx-auto block mt-2 h-10 text-sm text-gray-600"
            data-testid="scanner-save-btn"
          >
            Prüfen & Speichern
          </Button>
        </div>
      )}
    </div>
  );
}
